'use client';

import { useFormsTemplates } from '@commenda/api/form-builder/forms-templates';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { FormsTemplateStatus, FormsTemplateType } from '@commenda/types';
import { useState } from 'react';

interface AddStepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddNewStep: () => void;
  onAddSavedStep: (stepId: string) => void;
}

export function AddStepDialog({
  open,
  onOpenChange,
  onAddNewStep,
  onAddSavedStep,
}: AddStepDialogProps) {
  const [selectedSavedStepId, setSelectedSavedStepId] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Fetch saved steps for the dialog
  const { templates: savedSteps, isLoading: isLoadingSteps } =
    useFormsTemplates({
      type: [FormsTemplateType.FORM_STEP],
      status: [FormsTemplateStatus.LIVE, FormsTemplateStatus.COMPLETED],
    });

  // Filter saved steps to LIVE or COMPLETED
  const availableSavedSteps = savedSteps.filter(
    (s) =>
      s.status === FormsTemplateStatus.LIVE ||
      s.status === FormsTemplateStatus.COMPLETED
  );

  const handleAddNewStep = () => {
    onOpenChange(false);
    onAddNewStep();
    setSelectedSavedStepId('');
  };

  const handleAddSavedStep = () => {
    if (selectedSavedStepId) {
      onAddSavedStep(selectedSavedStepId);
      onOpenChange(false);
      setSelectedSavedStepId('');
    }
  };

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedSavedStepId('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="flex flex-col gap-xs justify-start items-start">
          <DialogTitle>Add Step</DialogTitle>
          <DialogDescription>
            Create a new step or use a saved step template.
          </DialogDescription>
        </DialogHeader>
        <div className="py-6 px-6 space-y-4">
          {/* Add New Step Option */}
          <div
            onClick={handleAddNewStep}
            className="p-4 border border-border-secondary rounded-lg hover:bg-bg-secondary cursor-pointer transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-bg-tertiary">
                <Icons.Plus className="w-5 h-5 text-text-secondary" />
              </div>
              <div>
                <p className="txt-small-medium text-text-primary">
                  Add New Step
                </p>
                <p className="txt-xs-regular text-text-tertiary">
                  Create a blank step with a default section
                </p>
              </div>
            </div>
          </div>

          {/* Use Saved Step Option */}
          {availableSavedSteps.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border-secondary" />
                <span className="txt-xs-regular text-text-tertiary">or</span>
                <div className="flex-1 h-px bg-border-secondary" />
              </div>

              <div className="space-y-2">
                <Label>Use Saved Step</Label>
                <SingleCombobox
                  options={availableSavedSteps.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  value={selectedSavedStepId}
                  setValue={(value) => setSelectedSavedStepId(value)}
                />
              </div>
            </div>
          )}

          {availableSavedSteps.length === 0 && !isLoadingSteps && (
            <p className="txt-xs-regular text-text-tertiary text-center">
              No saved steps available. Save a step from the Step Settings panel
              to reuse it here.
            </p>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          {selectedSavedStepId && (
            <Button variant="primary" onClick={handleAddSavedStep}>
              Add Saved Step
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddStepDialog;
